import { useState } from 'react';
import type { WebhookDelivery, WebhookDeliveryFilter, WebhookDeliveryStatus, WebhookEndpoint, WebhookEventType } from '@/api/types';
import type { Tone } from '@/components/ui';
import { formatDate, formatTime } from './format';
import { API_DOCS_URL } from './apiKeys';

/** Webhooks in «Интеграции» (MK42-6388): the cabinet's words for events, states and the log. */

export const WEBHOOK_DOCS_URL = `${API_DOCS_URL}#webhooks`;

export const EVENT_LABEL: Record<WebhookEventType, string> = {
  'invoice.issued': 'Выставлен счёт',
  'report.published': 'Опубликован отчёт',
  'task.needs_client': 'Задача ждёт вас',
  'test.ping': 'Проверка',
};
/** In the order the form offers them. */
export const OFFERED_EVENTS: WebhookEventType[] = ['invoice.issued', 'report.published', 'task.needs_client', 'test.ping'];

/** A type the cabinet does not know yet shows as its code. */
export const eventLabel = (t: string) => EVENT_LABEL[t as WebhookEventType] ?? t;

/** On, or why it is off — the reason in plain words. */
export function endpointState(ep: WebhookEndpoint): { tone: Tone; label: string; hint?: string } {
  if (ep.enabled) return { tone: 'done', label: 'Включён' };
  switch (ep.disabledReason) {
    case 'unverified':
      return { tone: 'waiting', label: 'Ждёт проверки', hint: 'Нажмите «Проверить»: вебхук включится, когда сервер ответит 2xx.' };
    case 'ping_failed':
      return { tone: 'overdue', label: 'Проверка не прошла', hint: 'Сервер не ответил 2xx на проверку. Почините и нажмите «Проверить» снова.' };
    case 'failing':
      return {
        tone: 'overdue',
        label: 'Выключен автоматически',
        hint: `3 дня сервер не принимал события${ep.disabledAt ? `, выключен ${formatDate(ep.disabledAt)}` : ''}. Почините сервер, включите вебхук и повторите неудачные за период.`,
      };
    case 'by_studio':
      return { tone: 'overdue', label: 'Выключен студией', hint: 'Когда сервер будет готов, нажмите «Проверить»: вебхук включится после ответа 2xx.' };
    default:
      return { tone: 'neutral', label: 'Выключен' };
  }
}

export const STATUS_LABEL: Record<WebhookDeliveryStatus, string> = {
  pending: 'В очереди',
  delivered: 'Доставлено',
  failed: 'Не доставлено',
  dead: 'Попытки кончились',
};
const STATUS_TONE: Record<WebhookDeliveryStatus, Tone> = { pending: 'waiting', delivered: 'done', failed: 'overdue', dead: 'overdue' };

export function deliveryState(d: WebhookDelivery): { tone: Tone; label: string } {
  return { tone: STATUS_TONE[d.status] ?? 'neutral', label: STATUS_LABEL[d.status] ?? d.status };
}

const ERROR_LABEL: Record<string, string> = {
  timeout: 'нет ответа за 10 с',
  connect: 'не соединиться',
  dns: 'имя не найдено',
  redirect: 'перенаправление',
  blocked_address: 'адрес во внутренней сети',
  endpoint_disabled: 'вебхук выключен',
};
/** The HTTP code, or the reason there was none. */
export function answerLabel(d: WebhookDelivery): string {
  if (d.responseCode) return String(d.responseCode);
  if (d.error) return ERROR_LABEL[d.error] ?? d.error;
  return '—';
}

export const whenLabel = (iso: string) => `${formatDate(iso)}, ${formatTime(iso)}`;

/** May «Повторить» go: a settled delivery that is not a check. */
export const replayable = (d: WebhookDelivery) => d.status !== 'pending' && d.type !== 'test.ping';

/** Pages of the log by `before`: a stack, so «Новее» goes back. A new filter starts over. */
export function useLogPaging() {
  const [filter, setFilter] = useState<WebhookDeliveryFilter>({});
  const [stack, setStack] = useState<(string | undefined)[]>([]);
  return {
    filter,
    setFilter: (f: WebhookDeliveryFilter) => (setStack([]), setFilter({ ...f, before: undefined })),
    older: (next: string | null | undefined) => {
      if (!next) return;
      setStack((s) => [...s, filter.before]);
      setFilter({ ...filter, before: next });
    },
    newer: stack.length
      ? () => {
          setFilter({ ...filter, before: stack[stack.length - 1] });
          setStack((s) => s.slice(0, -1));
        }
      : undefined,
  };
}
